import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, timer } from 'rxjs';
import { AuthService } from '../services/auth.service';

const GUEST_STATE_TIMEOUT_MS = 1000;

/**
 * Guest Guard (Unauthenticated Guard)
 *
 * Prevents authenticated users from accessing routes that should only be available
 * to unauthenticated users (like login page). Redirects authenticated users to dashboard.
 */
export const guestGuard: CanActivateFn = (route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return new Observable<boolean | UrlTree>((observer) => {
    // Check if already authenticated (synchronous check)
    if (authService.isAuthenticated) {
      observer.next(router.parseUrl('/dashboard'));
      observer.complete();
      return;
    }

    // Wait a reasonable time for auth state to be determined
    let authDetermined = false;

    const subscription = authService.isAuthenticated$.subscribe((isAuth) => {
      if (isAuth && !authDetermined) {
        authDetermined = true;
        observer.next(router.parseUrl('/dashboard'));
        observer.complete();
      }
    });

    // After timeout, if still not authenticated, allow access
    timer(GUEST_STATE_TIMEOUT_MS).subscribe(() => {
      if (!authDetermined && !authService.isAuthenticated) {
        authDetermined = true;
        subscription.unsubscribe();
        observer.next(true);
        observer.complete();
      }
    });

    return () => subscription.unsubscribe();
  });
};